import { InspectorIcon } from "../InspectorIcon";
import type { SVGProps } from "react";
import { useCallback, useId, useState, type ComponentType } from "react";
import {
  ArrowLeftIcon,
  ScanSearchIcon,
  BookOpenIcon,
  LayersIcon,
  ClipboardListIcon,
  LayoutDashboardIcon,
  LayoutTemplateIcon,
  ListChecksIcon,
  NewspaperIcon,
  ScrollTextIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { useNavigate, useParams } from "@tanstack/react-router";

import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "../ui/sidebar";

import { openGapCount, pendingRecordUpdateCount } from "@repo/shared/reviewCounts";
import { useReviewCounts } from "../../state/governance";
import { useGovernanceSearchGroups } from "../globalSearch";
import { searchPaletteGroups } from "../sidebar/paletteSidebarSearch";
import { SidebarSearch } from "../sidebar/SidebarSearch";
import { cn } from "~/lib/utils";
import { useRegulationOrg } from "./useRegulationOrg";

export type RegulationPath =
  | "/$org/compliance"
  | "/$org/compliance/findings"
  | "/$org/compliance/frameworks"
  | "/$org/compliance/regimes"
  | "/$org/compliance/inspector"
  | "/$org/compliance/knowledge"
  | "/$org/compliance/policies"
  | "/$org/compliance/procedures"
  | "/$org/compliance/templates"
  | "/$org/compliance/regulatory"
  | "/$org/compliance/suggestions";

export interface RegulationNavItem {
  readonly to: RegulationPath;
  readonly label: string;
  readonly icon: ComponentType<{ className?: string }>;
}

/**
 * Regulation's sections, grouped by the question each one answers.
 *
 * A flat list of seven made every section look equally weighted, which they
 * are not: the library is what you keep, the standards are what you are held
 * to, and the sources are where both come from. The labels are those three
 * questions — they mean something, so a section landing in the wrong group
 * would read as wrong.
 */
/** The Inspector mark in the sidebar's own ink, like the other nav icons. */
function InspectorNavIcon(props: SVGProps<SVGSVGElement>) {
  return <InspectorIcon ink="inherit" {...props} />;
}

export const REGULATION_NAV_GROUPS: ReadonlyArray<{
  /** Null for the lead item — Overview answers no question, it opens the space. */
  readonly label: string | null;
  readonly items: ReadonlyArray<RegulationNavItem>;
}> = [
  {
    label: null,
    // What waits on someone sits right under Overview; both count open items only.
    items: [
      { to: "/$org/compliance", label: "Overview", icon: LayoutDashboardIcon },
      { to: "/$org/compliance/findings", label: "Findings", icon: ScanSearchIcon },
      { to: "/$org/compliance/suggestions", label: "Suggestions", icon: ClipboardListIcon },
    ],
  },
  {
    label: "Library",
    items: [
      { to: "/$org/compliance/policies", label: "Policies", icon: ScrollTextIcon },
      { to: "/$org/compliance/procedures", label: "Procedures", icon: ListChecksIcon },
      { to: "/$org/compliance/knowledge", label: "Knowledge", icon: BookOpenIcon },
      { to: "/$org/compliance/templates", label: "Templates", icon: LayoutTemplateIcon },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/$org/compliance/regimes", label: "Regimes", icon: ShieldCheckIcon },
      { to: "/$org/compliance/frameworks", label: "Frameworks", icon: LayersIcon },
    ],
  },
  {
    label: "Sources",
    items: [
      { to: "/$org/compliance/regulatory", label: "Monitor", icon: NewspaperIcon },
      { to: "/$org/compliance/inspector", label: "Inspector", icon: InspectorNavIcon },
    ],
  },
];

/** Flat view of every section, for callers that only need the list. */
export const LIBRARY_NAV_ITEMS: ReadonlyArray<RegulationNavItem> = REGULATION_NAV_GROUPS.flatMap(
  (group) => group.items,
);

/** The label a pathname is currently on, for the header's breadcrumb. */
export function regulationSectionLabel(pathname: string, org: string | null): string | null {
  if (!org) return null;
  const match = LIBRARY_NAV_ITEMS.find(
    (item) => item.to !== "/$org/compliance" && isRegulationSectionActive(pathname, item.to, org),
  );
  return match?.label ?? null;
}

/** Segment boundaries prevent unrelated paths from selecting a section. */
export function isRegulationSectionActive(
  pathname: string,
  to: RegulationPath,
  org: string,
): boolean {
  const target = to.replace("/$org", `/${org}`);
  const path = pathname.replace(/\/$/, "");
  return path === target || (to !== "/$org/compliance" && path.startsWith(`${target}/`));
}

export function RegulationSidebarNav({ pathname }: { pathname: string }) {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  // The tenant comes from the URL these links live under, so navigating
  // between sections can never silently change organization.
  const org = useParams({ strict: false, select: (params) => (params as { org?: string }).org });

  const { org: organization } = useRegulationOrg();
  const organizationId = organization?.id ?? null;
  const reviews = useReviewCounts(organizationId);
  const countId = useId();
  // The search sources load once the field is armed (focused or hotkeyed).
  const [searchArmed, setSearchArmed] = useState(false);
  const armSearch = useCallback(() => setSearchArmed(true), []);
  const searchGroups = useGovernanceSearchGroups(searchArmed);
  // What still waits on a decision, not everything ever raised: a total that
  // includes decided items never goes down when the queue is worked through.
  const recordUpdates = reviews.snapshot ? pendingRecordUpdateCount(reviews.snapshot) : null;
  const gaps = reviews.snapshot ? openGapCount(reviews.snapshot) : null;
  // A count whose gap split the answer does not carry is unknown: no badge, never a guess.
  const counts: Partial<Record<RegulationPath, number>> = organizationId && reviews.unavailableReason === null
    ? {
        // Housekeeping only: gap suggestions count under Findings.
        ...(recordUpdates === null ? {} : { "/$org/compliance/suggestions": recordUpdates }),
        // Every gap: regulatory findings plus control amendments and missing documents.
        ...(gaps === null ? {} : { "/$org/compliance/findings": gaps }),
      }
    : {};

  const handleSectionClick = useCallback(
    (to: RegulationPath) => {
      if (isMobile) {
        setOpenMobile(false);
      }
      if (!org) return;
      void navigate({ to, params: { org }, replace: true });
    },
    [isMobile, navigate, org, setOpenMobile],
  );

  return (
    <SidebarContent className="overflow-x-hidden">
      <SidebarGroup className="gap-4 p-[var(--sidebar-content-inset)] pt-2.5">
        <SidebarSearch
          label="Search governance"
          emptyLabel="Nothing in governance matches"
          search={(query) => searchPaletteGroups(searchGroups, query)}
          onSelect={(result) => void result.run()}
          onArm={armSearch}
        >
        {REGULATION_NAV_GROUPS.map((group) => (
          <div key={group.label ?? "overview"} className="flex flex-col">
            {group.label !== null && (
              <SidebarGroupLabel className="h-7 text-sidebar-muted-foreground/70">
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarMenu className="ps-px">
              {group.items.map((item) => {
                const Icon = item.icon;
                const count = counts[item.to];
                const badgeId = `${countId}-${item.label}`;
                // Overview owns the space's root, so it must match exactly or
                // it would light up on every section beneath it.
                const isActive = org ? isRegulationSectionActive(pathname, item.to, org) : false;
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      isActive={isActive}
                      aria-label={item.label}
                      aria-describedby={count === undefined ? undefined : badgeId}
                      onClick={() => handleSectionClick(item.to)}
                    >
                      <Icon />
                      <span className="truncate">{item.label}</span>
                      {count !== undefined ? (
                        <span
                          id={badgeId}
                          data-sidebar="review-count"
                          className={cn(
                            "ms-auto shrink-0 pe-0.5 text-xs tabular-nums text-sidebar-muted-foreground group-data-[collapsible=icon]:hidden",
                            // Open findings are the urgent count: red while any are open.
                            item.to === "/$org/compliance/findings" && count > 0 && "font-medium text-destructive",
                          )}
                        >
                          {count.toLocaleString("en-US")}
                          <span className="sr-only"> open {item.label.toLowerCase()}</span>
                        </span>
                      ) : null}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </div>
        ))}
        </SidebarSearch>
      </SidebarGroup>
    </SidebarContent>
  );
}

/**
 * The way back out of the space — a real sidebar footer, below the scroll
 * area, so it sits at the very bottom regardless of how short the nav is.
 */
export function RegulationSidebarFooter() {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const org = useParams({ strict: false, select: (params) => (params as { org?: string }).org });

  return (
    <SidebarFooter className="p-[var(--sidebar-content-inset)]">
      <SidebarMenu className="ps-px">
        <SidebarMenuItem>
          <SidebarMenuButton
            onClick={() => {
              if (isMobile) setOpenMobile(false);
              if (!org) return;
              void navigate({ to: "/$org/chat", params: { org } });
            }}
          >
            <ArrowLeftIcon />
            <span className="truncate">Back to chat</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  );
}
